// src/components/SavedViews.tsx
/**
 * Timber Management System — SavedViews (shared component)
 *
 * Drop-in UI that manages "Saved Views" (filter presets) for any page.
 *
 * Usage:
 *   <SavedViews
 *     storageKey="opts:siteViews:v1"
 *     currentFilters={filters}
 *     onLoad={(next) => setFilters(next)}
 *   />
 *
 * Features
 * • List, Load, Save, Rename, Delete views (persisted via get/set)
 * • Toasts via useNotifications()
 * • Audit via logAudit(...)
 * • Accessible "popover" using <details>/<summary>, matching your design system
 *
 * Assumptions
 * • You have localStorage helpers at @/lib/storage (get, set)
 * • You have useNotifications() at @/lib/notifications with .push({ title, body?, level? })
 * • You have logAudit(payload, actorRole) at @/lib/audit
 * • Roles: Admin, Dispatcher, Loader, Trucker, Accountant (stored at 'role')
 *
 * Notes
 * • Generic <T> type for filters so each page can store its own shape.
 * • Views saved under `storageKey` you pass in (e.g., opts:siteViews:v1 / opts:jobViews:v1).
 */

import React, { ReactNode, useMemo, useState } from 'react'
import { ListFilter, Save, Pencil, Trash2 } from 'lucide-react'
import { get, set } from '@/lib/storage'
import { useNotifications } from '@/lib/notifications'
import { logAudit } from '@/lib/audit'

type Role = 'Admin'|'Dispatcher'|'Loader'|'Trucker'|'Accountant'
type SavedView<T> = { id: string; name: string; filters: T; createdAt: string; updatedAt: string }

type Props<T> = {
  /** LocalStorage key for this page's views, e.g. "opts:siteViews:v1" */
  storageKey: string
  /** The current filter state you'd like to save as a view */
  currentFilters: T
  /** Called with a view's filters when the user chooses "Load" */
  onLoad: (filters: T) => void
  /** Optional label shown in the button (defaults to "Saved Views") */
  label?: string
  /** Optional leading icon override (defaults to ListFilter) */
  icon?: ReactNode
  /** Optional className for the <details> wrapper */
  className?: string
  /** Optional role override; defaults to value in localStorage('role') or 'Admin' */
  actorRole?: Role
}

function cx(...xs: Array<string | undefined | false | null>) { return xs.filter(Boolean).join(' ') }
function uid() { return (globalThis.crypto && 'randomUUID' in globalThis.crypto) ? (crypto as any).randomUUID() : Math.random().toString(36).slice(2) }

export default function SavedViews<T>(props: Props<T>) {
  const {
    storageKey,
    currentFilters,
    onLoad,
    label = 'Saved Views',
    icon,
    className,
    actorRole,
  } = props

  const role: Role = (get<Role>('role', 'Admin' as Role) || 'Admin')
  const actor: Role = actorRole || role
  const { push } = useNotifications()

  const [views, setViews] = useState<SavedView<T>[]>(() => get<SavedView<T>[]>(storageKey, []))

  function persist(next: SavedView<T>[]) {
    setViews(next); set(storageKey, next)
  }

  function defaultName() {
    const base = 'View'
    let n = views.length + 1
    let candidate = `${base} ${n}`
    const names = new Set(views.map(v => v.name))
    while (names.has(candidate)) { n++; candidate = `${base} ${n}` }
    return candidate
  }

  function saveNew(name?: string) {
    const now = new Date().toISOString()
    const view: SavedView<T> = {
      id: uid(),
      name: (name && name.trim()) || defaultName(),
      filters: currentFilters,
      createdAt: now,
      updatedAt: now,
    }
    const next = [view, ...views]
    persist(next)
    push({ title: 'View saved', body: view.name })
    logAudit({ action: 'views:save', details: { storageKey, id: view.id, name: view.name } }, actor)
  }

  function load(id: string) {
    const v = views.find(x => x.id === id); if (!v) return
    onLoad(v.filters)
    push({ title: 'View loaded', body: v.name })
    logAudit({ action: 'views:load', details: { storageKey, id: v.id, name: v.name } }, actor)
  }

  function rename(id: string) {
    const v = views.find(x => x.id === id); if (!v) return
    const nextName = window.prompt('Rename view', v.name)?.trim()
    if (!nextName) return
    const next = views.map(x => x.id === id ? { ...x, name: nextName, updatedAt: new Date().toISOString() } : x)
    persist(next)
    push({ title: 'View renamed', body: nextName })
    logAudit({ action: 'views:rename', details: { storageKey, id: v.id, name: nextName } }, actor)
  }

  function remove(id: string) {
    const v = views.find(x => x.id === id); if (!v) return
    if (!confirm(`Delete view "${v.name}"?`)) return
    const next = views.filter(x => x.id !== id)
    persist(next)
    push({ title: 'View deleted' })
    logAudit({ action: 'views:delete', details: { storageKey, id: v.id, name: v.name } }, actor)
  }

  return (
    <div className={cx('popover relative', className)}>
      <details>
        <summary className="btn-secondary flex items-center gap-2 px-3 py-1.5">
          {icon || <ListFilter className="w-4 h-4" />}
          {label}
        </summary>

        <div className="popover p-2 mt-2 w-72">
          <div className="text-sm mb-2">Saved Views</div>

          {views.length === 0 ? (
            <div className="text-xs muted mb-3">
              No saved views yet. Save your current filters to reuse later.
            </div>
          ) : (
            <div className="flex flex-col gap-1 max-h-64 overflow-auto">
              {views.map(v => (
                <div key={v.id} className="flex items-center justify-between gap-2">
                  <button className="underline text-left truncate" title={v.name} onClick={() => load(v.id)}>
                    {v.name}
                  </button>
                  <div className="flex items-center gap-1">
                    <button className="btn-secondary px-2 py-1" title="Rename" onClick={() => rename(v.id)}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button className="btn-danger px-2 py-1" title="Delete" onClick={() => remove(v.id)}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-3">
            <button
              className="btn w-full flex items-center justify-center gap-2"
              onClick={() => saveNew()}
              title="Save current filters as a new view"
            >
              <Save className="w-4 h-4" />
              Save current as view
            </button>
          </div>
        </div>
      </details>
    </div>
  )
}
